/**
 * Whereby Tutorial Page
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const translations: Record<Locale, TutorialData> = {
  en: {
    pageTitle: 'Using Sokuji with Whereby',
    backLink: 'Back to Supported Sites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Overview',
      content: 'This guide will walk you through setting up and using Sokuji with Whereby for real-time language translation during your video meetings.',
    },
    steps: [
      {
        title: 'Install Sokuji Extension',
        content: 'Make sure you have the Sokuji extension installed in your Chrome or Edge browser. You can get it from the Chrome Web Store.',
        tip: 'The extension icon should appear in your browser toolbar after installation.',
      },
      {
        title: 'Open Whereby',
        content: 'Navigate to your Whereby room URL (e.g., <code>whereby.com/your-room</code>) in your browser.',
        tip: 'Whereby works entirely in the browser, making it perfect for use with Sokuji.',
      },
      {
        title: 'Join the Room',
        content: 'Enter your name and click Join to enter the Whereby room. You may be asked to allow camera and microphone access.',
        tip: 'Allow the browser to access your microphone when prompted.',
      },
      {
        title: 'Configure Audio Settings',
        content: 'In the meeting controls, click on Settings (gear icon). Under Microphone, select <strong>Sokuji Virtual Microphone</strong> from the dropdown.',
        tip: 'If the virtual microphone doesn\'t appear, refresh the page and ensure the Sokuji extension is active.',
      },
      {
        title: 'Start Speaking',
        content: 'Your speech will now be translated in real-time. Speak naturally and others in the room will hear the translated audio.',
        tip: 'Speak at a natural pace for the best translation quality.',
      },
    ],
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Use a good quality microphone for clear audio',
        'Test your setup before important meetings',
        'Keep sentences concise for more accurate translations',
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'Does Whereby require any special setup?',
          answer: 'No, Whereby works entirely in the browser. Simply navigate to your room URL and Sokuji will be available.',
        },
        {
          question: 'Can I use Sokuji in Whereby breakout rooms?',
          answer: 'Yes, Sokuji works in all Whereby rooms including breakout rooms.',
        },
        {
          question: 'Will Sokuji work with Whereby recordings?',
          answer: 'Yes, if you record your Whereby meeting, the translated audio will be included in the recording.',
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content: 'If you encounter any issues, please visit our <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub repository</a> or contact us at <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> for support.',
    },
  },
  zh: {
    pageTitle: '在 Whereby 中使用 Sokuji',
    backLink: '返回支持的网站',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概述',
      content: '本指南将指导您如何在 Whereby 中设置和使用 Sokuji，实现视频会议中的实时语言翻译。',
    },
    steps: [
      {
        title: '安装 Sokuji 扩展',
        content: '确保您已在 Chrome 或 Edge 浏览器中安装了 Sokuji 扩展。您可以从 Chrome 网上应用店获取。',
        tip: '安装后，扩展图标应出现在浏览器工具栏中。',
      },
      {
        title: '打开 Whereby',
        content: '在浏览器中访问您的 Whereby 房间 URL（例如 <code>whereby.com/your-room</code>）。',
        tip: 'Whereby 完全在浏览器中运行，非常适合与 Sokuji 一起使用。',
      },
      {
        title: '加入房间',
        content: '输入您的名字并点击加入以进入 Whereby 房间。可能会要求您允许摄像头和麦克风访问。',
        tip: '出现提示时允许浏览器访问您的麦克风。',
      },
      {
        title: '配置音频设置',
        content: '在会议控件中，点击设置（齿轮图标）。在麦克风下，从下拉菜单中选择 <strong>Sokuji Virtual Microphone</strong>。',
        tip: '如果虚拟麦克风没有出现，请刷新页面并确保 Sokuji 扩展处于活动状态。',
      },
      {
        title: '开始说话',
        content: '您的语音现在将被实时翻译。自然地说话，房间中的其他人将听到翻译后的音频。',
        tip: '以自然的速度说话以获得最佳翻译质量。',
      },
    ],
    tips: {
      title: '最佳使用建议',
      items: [
        '使用高质量麦克风以获得清晰的音频',
        '在重要会议前测试您的设置',
        '保持句子简洁以获得更准确的翻译',
      ],
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: 'Whereby 需要特殊设置吗？',
          answer: '不需要，Whereby 完全在浏览器中运行。只需导航到您的房间 URL，Sokuji 即可使用。',
        },
        {
          question: '我可以在 Whereby 分组讨论室中使用 Sokuji 吗？',
          answer: '是的，Sokuji 可以在所有 Whereby 房间中使用，包括分组讨论室。',
        },
        {
          question: 'Sokuji 支持 Whereby 录制吗？',
          answer: '是的，如果您录制 Whereby 会议，翻译后的音频将包含在录制中。',
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content: '如果遇到任何问题，请访问我们的 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 仓库</a> 或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> 获取支持。',
    },
  },
  ja: {
    pageTitle: 'Whereby で Sokuji を使用する',
    backLink: 'サポートされているサイトに戻る',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概要',
      content: 'このガイドでは、Whereby で Sokuji を設定して使用し、ビデオ会議中にリアルタイム言語翻訳を行う方法を説明します。',
    },
    steps: [
      {
        title: 'Sokuji 拡張機能をインストール',
        content: 'Chrome または Edge ブラウザに Sokuji 拡張機能がインストールされていることを確認してください。Chrome ウェブストアから入手できます。',
        tip: 'インストール後、拡張機能のアイコンがブラウザのツールバーに表示されます。',
      },
      {
        title: 'Whereby を開く',
        content: 'ブラウザで Whereby ルームの URL（例：<code>whereby.com/your-room</code>）にアクセスします。',
        tip: 'Whereby は完全にブラウザで動作するため、Sokuji との使用に最適です。',
      },
      {
        title: 'ルームに参加',
        content: '名前を入力し、参加をクリックして Whereby ルームに入室します。カメラとマイクのアクセスを許可するよう求められる場合があります。',
        tip: 'プロンプトが表示されたら、ブラウザがマイクにアクセスすることを許可してください。',
      },
      {
        title: 'オーディオ設定を構成',
        content: '会議コントロールで、設定（歯車アイコン）をクリックします。マイクの下で、ドロップダウンから <strong>Sokuji Virtual Microphone</strong> を選択します。',
        tip: '仮想マイクが表示されない場合は、ページを更新し、Sokuji 拡張機能がアクティブであることを確認してください。',
      },
      {
        title: '話し始める',
        content: '音声がリアルタイムで翻訳されるようになります。自然に話すと、ルームの他の人は翻訳された音声を聞くことができます。',
        tip: '最適な翻訳品質のために自然なペースで話してください。',
      },
    ],
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        'クリアなオーディオのために高品質のマイクを使用する',
        '重要な会議の前に設定をテストする',
        'より正確な翻訳のために文を簡潔に保つ',
      ],
    },
    faq: {
      title: 'よくある質問',
      items: [
        {
          question: 'Whereby には特別なセットアップが必要ですか？',
          answer: 'いいえ、Whereby は完全にブラウザで動作します。ルーム URL にアクセスするだけで Sokuji が利用可能になります。',
        },
        {
          question: 'Whereby のブレイクアウトルームで Sokuji を使用できますか？',
          answer: 'はい、Sokuji はブレイクアウトルームを含むすべての Whereby ルームで動作します。',
        },
        {
          question: 'Sokuji は Whereby の録画で動作しますか？',
          answer: 'はい、Whereby 会議を録画すると、翻訳された音声が録画に含まれます。',
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content: '問題が発生した場合は、<a href="https://github.com/kizuna-ai-lab/sokuji">GitHub リポジトリ</a>を訪問するか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までメールでお問い合わせください。',
    },
  },
  ko: {
    pageTitle: 'Whereby에서 Sokuji 사용하기',
    backLink: '지원 사이트로 돌아가기',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '개요',
      content: '이 가이드는 Whereby에서 Sokuji를 설정하고 사용하여 비디오 회의 중 실시간 언어 번역을 수행하는 방법을 안내합니다.',
    },
    steps: [
      {
        title: 'Sokuji 확장 프로그램 설치',
        content: 'Chrome 또는 Edge 브라우저에 Sokuji 확장 프로그램이 설치되어 있는지 확인하세요. Chrome 웹 스토어에서 받을 수 있습니다.',
        tip: '설치 후 확장 프로그램 아이콘이 브라우저 도구 모음에 나타납니다.',
      },
      {
        title: 'Whereby 열기',
        content: '브라우저에서 Whereby 룸 URL(예: <code>whereby.com/your-room</code>)로 이동하세요.',
        tip: 'Whereby는 전적으로 브라우저에서 작동하므로 Sokuji와 함께 사용하기에 완벽합니다.',
      },
      {
        title: '룸 참여',
        content: '이름을 입력하고 참여를 클릭하여 Whereby 룸에 입장하세요. 카메라와 마이크 액세스를 허용하라는 메시지가 표시될 수 있습니다.',
        tip: '메시지가 표시되면 브라우저가 마이크에 액세스하도록 허용하세요.',
      },
      {
        title: '오디오 설정 구성',
        content: '회의 컨트롤에서 설정(톱니바퀴 아이콘)을 클릭하세요. 마이크에서 드롭다운에서 <strong>Sokuji Virtual Microphone</strong>을 선택하세요.',
        tip: '가상 마이크가 나타나지 않으면 페이지를 새로 고치고 Sokuji 확장 프로그램이 활성화되어 있는지 확인하세요.',
      },
      {
        title: '말하기 시작',
        content: '이제 음성이 실시간으로 번역됩니다. 자연스럽게 말하면 룸의 다른 사람들이 번역된 오디오를 듣게 됩니다.',
        tip: '최적의 번역 품질을 위해 자연스러운 속도로 말하세요.',
      },
    ],
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '깨끗한 오디오를 위해 고품질 마이크 사용',
        '중요한 회의 전에 설정 테스트',
        '더 정확한 번역을 위해 문장을 간결하게 유지',
      ],
    },
    faq: {
      title: '자주 묻는 질문',
      items: [
        {
          question: 'Whereby에 특별한 설정이 필요한가요?',
          answer: '아니요, Whereby는 전적으로 브라우저에서 작동합니다. 룸 URL로 이동하기만 하면 Sokuji를 사용할 수 있습니다.',
        },
        {
          question: 'Whereby 브레이크아웃 룸에서 Sokuji를 사용할 수 있나요?',
          answer: '네, Sokuji는 브레이크아웃 룸을 포함한 모든 Whereby 룸에서 작동합니다.',
        },
        {
          question: 'Sokuji가 Whereby 녹화에서 작동하나요?',
          answer: '네, Whereby 회의를 녹화하면 번역된 오디오가 녹화에 포함됩니다.',
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content: '문제가 발생하면 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 저장소</a>를 방문하거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 지원을 받으세요.',
    },
  },
  es: {
    pageTitle: 'Usar Sokuji con Whereby',
    backLink: 'Volver a sitios compatibles',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Descripción general',
      content: 'Esta guía le explicará cómo configurar y usar Sokuji con Whereby para la traducción de idiomas en tiempo real durante sus videollamadas.',
    },
    steps: [
      {
        title: 'Instalar la extensión Sokuji',
        content: 'Asegúrese de tener la extensión Sokuji instalada en su navegador Chrome o Edge. Puede obtenerla en la Chrome Web Store.',
        tip: 'El icono de la extensión debería aparecer en la barra de herramientas de su navegador después de la instalación.',
      },
      {
        title: 'Abrir Whereby',
        content: 'Navegue a la URL de su sala de Whereby (p. ej., <code>whereby.com/your-room</code>) en su navegador.',
        tip: 'Whereby funciona completamente en el navegador, lo que lo hace perfecto para usar con Sokuji.',
      },
      {
        title: 'Unirse a la sala',
        content: 'Ingrese su nombre y haga clic en Unirse para entrar a la sala de Whereby. Es posible que se le solicite permitir el acceso a la cámara y el micrófono.',
        tip: 'Permita que el navegador acceda a su micrófono cuando se le solicite.',
      },
      {
        title: 'Configurar los ajustes de audio',
        content: 'En los controles de la reunión, haga clic en Configuración (icono de engranaje). En Micrófono, seleccione <strong>Sokuji Virtual Microphone</strong> del menú desplegable.',
        tip: 'Si el micrófono virtual no aparece, actualice la página y asegúrese de que la extensión Sokuji esté activa.',
      },
      {
        title: 'Empezar a hablar',
        content: 'Su voz ahora será traducida en tiempo real. Hable de forma natural y los demás en la sala escucharán el audio traducido.',
        tip: 'Hable a un ritmo natural para obtener la mejor calidad de traducción.',
      },
    ],
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Use un micrófono de buena calidad para un audio claro',
        'Pruebe su configuración antes de reuniones importantes',
        'Mantenga las oraciones concisas para traducciones más precisas',
      ],
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '¿Whereby requiere alguna configuración especial?',
          answer: 'No, Whereby funciona completamente en el navegador. Simplemente navegue a la URL de su sala y Sokuji estará disponible.',
        },
        {
          question: '¿Puedo usar Sokuji en las salas de grupo de Whereby?',
          answer: 'Sí, Sokuji funciona en todas las salas de Whereby, incluidas las salas de grupo.',
        },
        {
          question: '¿Sokuji funcionará con las grabaciones de Whereby?',
          answer: 'Sí, si graba su reunión de Whereby, el audio traducido se incluirá en la grabación.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      content: 'Si encuentra algún problema, visite nuestro <a href="https://github.com/kizuna-ai-lab/sokuji">repositorio de GitHub</a> o contáctenos en <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obtener ayuda.',
    },
  },
  fr: {
    pageTitle: 'Utiliser Sokuji avec Whereby',
    backLink: 'Retour aux sites pris en charge',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Aperçu',
      content: 'Ce guide vous expliquera comment configurer et utiliser Sokuji avec Whereby pour la traduction linguistique en temps réel lors de vos réunions vidéo.',
    },
    steps: [
      {
        title: 'Installer l\'extension Sokuji',
        content: 'Assurez-vous que l\'extension Sokuji est installée dans votre navigateur Chrome ou Edge. Vous pouvez l\'obtenir sur le Chrome Web Store.',
        tip: 'L\'icône de l\'extension devrait apparaître dans la barre d\'outils de votre navigateur après l\'installation.',
      },
      {
        title: 'Ouvrir Whereby',
        content: 'Accédez à l\'URL de votre salle Whereby (par ex., <code>whereby.com/your-room</code>) dans votre navigateur.',
        tip: 'Whereby fonctionne entièrement dans le navigateur, ce qui le rend parfait pour une utilisation avec Sokuji.',
      },
      {
        title: 'Rejoindre la salle',
        content: 'Entrez votre nom et cliquez sur Rejoindre pour entrer dans la salle Whereby. Il peut vous être demandé d\'autoriser l\'accès à la caméra et au microphone.',
        tip: 'Autorisez le navigateur à accéder à votre microphone lorsqu\'on vous le demande.',
      },
      {
        title: 'Configurer les paramètres audio',
        content: 'Dans les commandes de la réunion, cliquez sur Paramètres (icône d\'engrenage). Sous Microphone, sélectionnez <strong>Sokuji Virtual Microphone</strong> dans le menu déroulant.',
        tip: 'Si le microphone virtuel n\'apparaît pas, actualisez la page et assurez-vous que l\'extension Sokuji est active.',
      },
      {
        title: 'Commencer à parler',
        content: 'Votre voix sera maintenant traduite en temps réel. Parlez naturellement et les autres dans la salle entendront l\'audio traduit.',
        tip: 'Parlez à un rythme naturel pour une qualité de traduction optimale.',
      },
    ],
    tips: {
      title: 'Conseils pour de meilleurs résultats',
      items: [
        'Utilisez un microphone de bonne qualité pour un audio clair',
        'Testez votre configuration avant les réunions importantes',
        'Gardez les phrases concises pour des traductions plus précises',
      ],
    },
    faq: {
      title: 'Questions fréquemment posées',
      items: [
        {
          question: 'Whereby nécessite-t-il une configuration spéciale ?',
          answer: 'Non, Whereby fonctionne entièrement dans le navigateur. Naviguez simplement vers l\'URL de votre salle et Sokuji sera disponible.',
        },
        {
          question: 'Puis-je utiliser Sokuji dans les salles de sous-groupes Whereby ?',
          answer: 'Oui, Sokuji fonctionne dans toutes les salles Whereby, y compris les salles de sous-groupes.',
        },
        {
          question: 'Sokuji fonctionnera-t-il avec les enregistrements Whereby ?',
          answer: 'Oui, si vous enregistrez votre réunion Whereby, l\'audio traduit sera inclus dans l\'enregistrement.',
        },
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      content: 'Si vous rencontrez des problèmes, veuillez visiter notre <a href="https://github.com/kizuna-ai-lab/sokuji">dépôt GitHub</a> ou nous contacter à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> pour obtenir de l\'aide.',
    },
  },
  ar: {
    pageTitle: 'استخدام Sokuji مع Whereby',
    backLink: 'العودة إلى المواقع المدعومة',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'نظرة عامة',
      content: 'سيرشدك هذا الدليل خلال إعداد واستخدام Sokuji مع Whereby للترجمة اللغوية الفورية أثناء اجتماعات الفيديو.',
    },
    steps: [
      {
        title: 'تثبيت إضافة Sokuji',
        content: 'تأكد من تثبيت إضافة Sokuji في متصفح Chrome أو Edge. يمكنك الحصول عليها من سوق Chrome الإلكتروني.',
        tip: 'يجب أن يظهر رمز الإضافة في شريط أدوات المتصفح بعد التثبيت.',
      },
      {
        title: 'فتح Whereby',
        content: 'انتقل إلى رابط غرفة Whereby الخاصة بك (مثل <code>whereby.com/your-room</code>) في متصفحك.',
        tip: 'يعمل Whereby بالكامل في المتصفح، مما يجعله مثاليًا للاستخدام مع Sokuji.',
      },
      {
        title: 'الانضمام إلى الغرفة',
        content: 'أدخل اسمك وانقر على انضمام للدخول إلى غرفة Whereby. قد يُطلب منك السماح بالوصول إلى الكاميرا والميكروفون.',
        tip: 'اسمح للمتصفح بالوصول إلى الميكروفون عند المطالبة.',
      },
      {
        title: 'تكوين إعدادات الصوت',
        content: 'في عناصر التحكم بالاجتماع، انقر على الإعدادات (أيقونة الترس). تحت الميكروفون، اختر <strong>Sokuji Virtual Microphone</strong> من القائمة المنسدلة.',
        tip: 'إذا لم يظهر الميكروفون الافتراضي، قم بتحديث الصفحة وتأكد من أن إضافة Sokuji نشطة.',
      },
      {
        title: 'ابدأ بالتحدث',
        content: 'سيتم الآن ترجمة كلامك في الوقت الفعلي. تحدث بشكل طبيعي وسيسمع الآخرون في الغرفة الصوت المترجم.',
        tip: 'تحدث بوتيرة طبيعية للحصول على أفضل جودة ترجمة.',
      },
    ],
    tips: {
      title: 'نصائح للحصول على أفضل النتائج',
      items: [
        'استخدم ميكروفون عالي الجودة للحصول على صوت واضح',
        'اختبر إعداداتك قبل الاجتماعات المهمة',
        'حافظ على الجمل مختصرة لترجمات أكثر دقة',
      ],
    },
    faq: {
      title: 'الأسئلة الشائعة',
      items: [
        {
          question: 'هل يتطلب Whereby أي إعداد خاص؟',
          answer: 'لا، يعمل Whereby بالكامل في المتصفح. ما عليك سوى الانتقال إلى رابط غرفتك وسيكون Sokuji متاحًا.',
        },
        {
          question: 'هل يمكنني استخدام Sokuji في غرف Whereby الفرعية؟',
          answer: 'نعم، يعمل Sokuji في جميع غرف Whereby بما في ذلك الغرف الفرعية.',
        },
        {
          question: 'هل سيعمل Sokuji مع تسجيلات Whereby؟',
          answer: 'نعم، إذا قمت بتسجيل اجتماع Whereby الخاص بك، سيتم تضمين الصوت المترجم في التسجيل.',
        },
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      content: 'إذا واجهت أي مشاكل، يرجى زيارة <a href="https://github.com/kizuna-ai-lab/sokuji">مستودع GitHub</a> الخاص بنا أو التواصل معنا عبر <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> للحصول على الدعم.',
    },
  },
  pt: {
    pageTitle: 'Usando o Sokuji com o Whereby',
    backLink: 'Voltar para sites compatíveis',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Visão geral',
      content: 'Este guia vai orientá-lo na configuração e uso do Sokuji com o Whereby para tradução de idiomas em tempo real durante suas reuniões por vídeo.',
    },
    steps: [
      {
        title: 'Instalar a extensão Sokuji',
        content: 'Certifique-se de que a extensão Sokuji está instalada no seu navegador Chrome ou Edge. Você pode obtê-la na Chrome Web Store.',
        tip: 'O ícone da extensão deve aparecer na barra de ferramentas do seu navegador após a instalação.',
      },
      {
        title: 'Abrir o Whereby',
        content: 'Navegue até a URL da sua sala Whereby (por exemplo, <code>whereby.com/your-room</code>) no seu navegador.',
        tip: 'O Whereby funciona inteiramente no navegador, tornando-o perfeito para usar com o Sokuji.',
      },
      {
        title: 'Entrar na sala',
        content: 'Digite seu nome e clique em Entrar para acessar a sala Whereby. Pode ser solicitado que você permita o acesso à câmera e ao microfone.',
        tip: 'Permita que o navegador acesse seu microfone quando solicitado.',
      },
      {
        title: 'Configurar as definições de áudio',
        content: 'Nos controles da reunião, clique em Configurações (ícone de engrenagem). Em Microfone, selecione <strong>Sokuji Virtual Microphone</strong> no menu suspenso.',
        tip: 'Se o microfone virtual não aparecer, atualize a página e certifique-se de que a extensão Sokuji está ativa.',
      },
      {
        title: 'Começar a falar',
        content: 'Sua fala agora será traduzida em tempo real. Fale naturalmente e os outros na sala ouvirão o áudio traduzido.',
        tip: 'Fale em um ritmo natural para obter a melhor qualidade de tradução.',
      },
    ],
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Use um microfone de boa qualidade para áudio claro',
        'Teste sua configuração antes de reuniões importantes',
        'Mantenha as frases concisas para traduções mais precisas',
      ],
    },
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          question: 'O Whereby requer alguma configuração especial?',
          answer: 'Não, o Whereby funciona inteiramente no navegador. Basta navegar até a URL da sua sala e o Sokuji estará disponível.',
        },
        {
          question: 'Posso usar o Sokuji nas salas de grupo do Whereby?',
          answer: 'Sim, o Sokuji funciona em todas as salas do Whereby, incluindo salas de grupo.',
        },
        {
          question: 'O Sokuji funcionará com as gravações do Whereby?',
          answer: 'Sim, se você gravar sua reunião do Whereby, o áudio traduzido será incluído na gravação.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      content: 'Se encontrar algum problema, visite nosso <a href="https://github.com/kizuna-ai-lab/sokuji">repositório no GitHub</a> ou entre em contato conosco em <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obter suporte.',
    },
  },
  ru: {
    pageTitle: 'Использование Sokuji с Whereby',
    backLink: 'Назад к поддерживаемым сайтам',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить и использовать Sokuji с Whereby для перевода в реальном времени во время видеоконференций.',
    },
    steps: [
      {
        title: 'Установите расширение Sokuji',
        content: 'Убедитесь, что расширение Sokuji установлено в вашем браузере Chrome или Edge. Вы можете получить его в Интернет-магазине Chrome.',
        tip: 'После установки значок расширения должен появиться на панели инструментов браузера.',
      },
      {
        title: 'Откройте Whereby',
        content: 'Перейдите по URL вашей комнаты Whereby (например, <code>whereby.com/your-room</code>) в браузере.',
        tip: 'Whereby работает полностью в браузере, что делает его идеальным для использования с Sokuji.',
      },
      {
        title: 'Присоединитесь к комнате',
        content: 'Введите своё имя и нажмите «Присоединиться», чтобы войти в комнату Whereby. Вас могут попросить разрешить доступ к камере и микрофону.',
        tip: 'Разрешите браузеру доступ к микрофону при появлении запроса.',
      },
      {
        title: 'Настройте параметры аудио',
        content: 'В элементах управления конференцией нажмите на Настройки (значок шестерёнки). В разделе Микрофон выберите <strong>Sokuji Virtual Microphone</strong> из выпадающего меню.',
        tip: 'Если виртуальный микрофон не отображается, обновите страницу и убедитесь, что расширение Sokuji активно.',
      },
      {
        title: 'Начните говорить',
        content: 'Теперь ваша речь будет переводиться в реальном времени. Говорите естественно, и остальные в комнате услышат переведённый звук.',
        tip: 'Говорите в естественном темпе для оптимального качества перевода.',
      },
    ],
    tips: {
      title: 'Советы для лучших результатов',
      items: [
        'Используйте качественный микрофон для чистого звука',
        'Протестируйте настройки перед важными встречами',
        'Формулируйте предложения кратко для более точного перевода',
      ],
    },
    faq: {
      title: 'Часто задаваемые вопросы',
      items: [
        {
          question: 'Требуется ли для Whereby специальная настройка?',
          answer: 'Нет, Whereby работает полностью в браузере. Просто перейдите по URL вашей комнаты, и Sokuji будет доступен.',
        },
        {
          question: 'Могу ли я использовать Sokuji в комнатах для групп Whereby?',
          answer: 'Да, Sokuji работает во всех комнатах Whereby, включая комнаты для групп.',
        },
        {
          question: 'Будет ли Sokuji работать с записями Whereby?',
          answer: 'Да, если вы запишете конференцию Whereby, переведённый звук будет включён в запись.',
        },
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      content: 'Если вы столкнулись с проблемами, посетите наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторий на GitHub</a> или свяжитесь с нами по адресу <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для получения поддержки.',
    },
  },
  de: {
    pageTitle: 'Sokuji mit Whereby verwenden',
    backLink: 'Zurück zu unterstützten Seiten',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Überblick',
      content: 'Diese Anleitung führt Sie durch die Einrichtung und Verwendung von Sokuji mit Whereby für Echtzeit-Sprachübersetzung während Ihrer Videokonferenzen.',
    },
    steps: [
      {
        title: 'Sokuji-Erweiterung installieren',
        content: 'Stellen Sie sicher, dass die Sokuji-Erweiterung in Ihrem Chrome- oder Edge-Browser installiert ist. Sie können sie im Chrome Web Store erhalten.',
        tip: 'Das Symbol der Erweiterung sollte nach der Installation in der Symbolleiste Ihres Browsers erscheinen.',
      },
      {
        title: 'Whereby öffnen',
        content: 'Navigieren Sie zur URL Ihres Whereby-Raums (z. B. <code>whereby.com/your-room</code>) in Ihrem Browser.',
        tip: 'Whereby funktioniert vollständig im Browser und eignet sich daher perfekt für die Verwendung mit Sokuji.',
      },
      {
        title: 'Dem Raum beitreten',
        content: 'Geben Sie Ihren Namen ein und klicken Sie auf Beitreten, um den Whereby-Raum zu betreten. Möglicherweise werden Sie gebeten, den Zugriff auf Kamera und Mikrofon zu erlauben.',
        tip: 'Erlauben Sie dem Browser den Zugriff auf Ihr Mikrofon, wenn Sie dazu aufgefordert werden.',
      },
      {
        title: 'Audioeinstellungen konfigurieren',
        content: 'Klicken Sie in den Meeting-Steuerelementen auf Einstellungen (Zahnradsymbol). Wählen Sie unter Mikrofon <strong>Sokuji Virtual Microphone</strong> aus dem Dropdown-Menü.',
        tip: 'Wenn das virtuelle Mikrofon nicht angezeigt wird, aktualisieren Sie die Seite und stellen Sie sicher, dass die Sokuji-Erweiterung aktiv ist.',
      },
      {
        title: 'Sprechen Sie los',
        content: 'Ihre Sprache wird jetzt in Echtzeit übersetzt. Sprechen Sie natürlich und die anderen im Raum werden das übersetzte Audio hören.',
        tip: 'Sprechen Sie in einem natürlichen Tempo für optimale Übersetzungsqualität.',
      },
    ],
    tips: {
      title: 'Tipps für beste Ergebnisse',
      items: [
        'Verwenden Sie ein hochwertiges Mikrofon für klaren Ton',
        'Testen Sie Ihre Einrichtung vor wichtigen Meetings',
        'Halten Sie Sätze kurz und prägnant für genauere Übersetzungen',
      ],
    },
    faq: {
      title: 'Häufig gestellte Fragen',
      items: [
        {
          question: 'Erfordert Whereby eine spezielle Einrichtung?',
          answer: 'Nein, Whereby funktioniert vollständig im Browser. Navigieren Sie einfach zu Ihrer Raum-URL und Sokuji ist verfügbar.',
        },
        {
          question: 'Kann ich Sokuji in Whereby-Breakout-Räumen verwenden?',
          answer: 'Ja, Sokuji funktioniert in allen Whereby-Räumen einschließlich Breakout-Räumen.',
        },
        {
          question: 'Funktioniert Sokuji mit Whereby-Aufnahmen?',
          answer: 'Ja, wenn Sie Ihr Whereby-Meeting aufzeichnen, wird das übersetzte Audio in der Aufnahme enthalten sein.',
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content: 'Wenn Sie auf Probleme stoßen, besuchen Sie bitte unser <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub-Repository</a> oder kontaktieren Sie uns unter <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> für Unterstützung.',
    },
  },
  uk: {
    pageTitle: 'Використання Sokuji з Whereby',
    backLink: 'Назад до підтримуваних сайтів',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати та використовувати Sokuji з Whereby для перекладу мов у реальному часі під час відеозустрічей.',
    },
    steps: [
      {
        title: 'Встановіть розширення Sokuji',
        content: 'Переконайтеся, що розширення Sokuji встановлено у вашому браузері Chrome або Edge. Ви можете отримати його в Інтернет-магазині Chrome.',
        tip: 'Після встановлення значок розширення повинен з\'явитися на панелі інструментів браузера.',
      },
      {
        title: 'Відкрийте Whereby',
        content: 'Перейдіть за URL вашої кімнати Whereby (наприклад, <code>whereby.com/your-room</code>) у браузері.',
        tip: 'Whereby працює повністю в браузері, що робить його ідеальним для використання з Sokuji.',
      },
      {
        title: 'Приєднайтеся до кімнати',
        content: 'Введіть своє ім\'я та натисніть «Приєднатися», щоб увійти до кімнати Whereby. Вас можуть попросити дозволити доступ до камери та мікрофона.',
        tip: 'Дозвольте браузеру доступ до мікрофона, коли з\'явиться запит.',
      },
      {
        title: 'Налаштуйте параметри аудіо',
        content: 'В елементах керування зустріччю натисніть на Налаштування (значок шестірні). У розділі Мікрофон виберіть <strong>Sokuji Virtual Microphone</strong> зі спадного меню.',
        tip: 'Якщо віртуальний мікрофон не з\'являється, оновіть сторінку та переконайтеся, що розширення Sokuji активне.',
      },
      {
        title: 'Почніть говорити',
        content: 'Тепер ваше мовлення буде перекладатися в реальному часі. Говоріть природно, і інші в кімнаті почують перекладене аудіо.',
        tip: 'Говоріть у природному темпі для оптимальної якості перекладу.',
      },
    ],
    tips: {
      title: 'Поради для кращих результатів',
      items: [
        'Використовуйте якісний мікрофон для чистого звуку',
        'Протестуйте налаштування перед важливими зустрічами',
        'Формулюйте речення стисло для точнішого перекладу',
      ],
    },
    faq: {
      title: 'Часті запитання',
      items: [
        {
          question: 'Чи потрібне для Whereby спеціальне налаштування?',
          answer: 'Ні, Whereby працює повністю в браузері. Просто перейдіть за URL вашої кімнати, і Sokuji буде доступний.',
        },
        {
          question: 'Чи можу я використовувати Sokuji в кімнатах для груп Whereby?',
          answer: 'Так, Sokuji працює в усіх кімнатах Whereby, включаючи кімнати для груп.',
        },
        {
          question: 'Чи працюватиме Sokuji із записами Whereby?',
          answer: 'Так, якщо ви записуєте зустріч Whereby, перекладене аудіо буде включено в запис.',
        },
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      content: 'Якщо у вас виникли проблеми, відвідайте наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторій на GitHub</a> або зв\'яжіться з нами за адресою <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для отримання підтримки.',
    },
  },
  it: {
    pageTitle: 'Usare Sokuji con Whereby',
    backLink: 'Torna ai siti supportati',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Panoramica',
      content: 'Questa guida ti spiegherà come configurare e utilizzare Sokuji con Whereby per la traduzione linguistica in tempo reale durante le tue riunioni video.',
    },
    steps: [
      {
        title: 'Installare l\'estensione Sokuji',
        content: 'Assicurati di avere l\'estensione Sokuji installata nel tuo browser Chrome o Edge. Puoi ottenerla dal Chrome Web Store.',
        tip: 'L\'icona dell\'estensione dovrebbe apparire nella barra degli strumenti del browser dopo l\'installazione.',
      },
      {
        title: 'Aprire Whereby',
        content: 'Vai all\'URL della tua stanza Whereby (ad es., <code>whereby.com/your-room</code>) nel tuo browser.',
        tip: 'Whereby funziona interamente nel browser, rendendolo perfetto per l\'uso con Sokuji.',
      },
      {
        title: 'Entrare nella stanza',
        content: 'Inserisci il tuo nome e fai clic su Entra per accedere alla stanza Whereby. Potrebbe esserti chiesto di consentire l\'accesso alla fotocamera e al microfono.',
        tip: 'Consenti al browser di accedere al microfono quando richiesto.',
      },
      {
        title: 'Configurare le impostazioni audio',
        content: 'Nei controlli della riunione, fai clic su Impostazioni (icona dell\'ingranaggio). Sotto Microfono, seleziona <strong>Sokuji Virtual Microphone</strong> dal menu a discesa.',
        tip: 'Se il microfono virtuale non appare, aggiorna la pagina e assicurati che l\'estensione Sokuji sia attiva.',
      },
      {
        title: 'Iniziare a parlare',
        content: 'Il tuo discorso verrà ora tradotto in tempo reale. Parla naturalmente e gli altri nella stanza sentiranno l\'audio tradotto.',
        tip: 'Parla a un ritmo naturale per una qualità di traduzione ottimale.',
      },
    ],
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Usa un microfono di buona qualità per un audio chiaro',
        'Testa la tua configurazione prima di riunioni importanti',
        'Mantieni le frasi concise per traduzioni più accurate',
      ],
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        {
          question: 'Whereby richiede una configurazione speciale?',
          answer: 'No, Whereby funziona interamente nel browser. Basta navigare all\'URL della tua stanza e Sokuji sarà disponibile.',
        },
        {
          question: 'Posso usare Sokuji nelle stanze breakout di Whereby?',
          answer: 'Sì, Sokuji funziona in tutte le stanze Whereby, comprese le stanze breakout.',
        },
        {
          question: 'Sokuji funzionerà con le registrazioni di Whereby?',
          answer: 'Sì, se registri la tua riunione Whereby, l\'audio tradotto sarà incluso nella registrazione.',
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content: 'Se riscontri problemi, visita il nostro <a href="https://github.com/kizuna-ai-lab/sokuji">repository GitHub</a> o contattaci all\'indirizzo <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> per ricevere assistenza.',
    },
  },
};

export function WherebyTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/whereby" />;
}
